import React from "react";
import {
  Paper,
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Hidden,
  CircularProgress,
} from "@material-ui/core";

const DynamicTable = ({
  title,
  headerActions,
  data = [],
  columns = [],
  rowKey = "id",
  actions,
  onRowClick,
  loading = false,
  emptyMessage = "Aucune donnée disponible",
  displayHeader = false,
  equalRowHeight = true,
}) => {
  const renderResponsiveHeader = () => {
    if (displayHeader === "none") return null;

    const header = (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={1.5}
        py={1}
        style={{
          minHeight: "var(--dt-header-min-height, 20px)",
          height: "var(--dt-header-height, 25px)",
          backgroundColor: "var(--dt-header-bg, #f5f5f5)",
          borderBottom: "var(--dt-header-border, 1px solid #ddd)",
          width: "var(--dt-header-width, 98%)",
          margin: "var(--dt-header-margin, 5px auto)",
        }}
      >
        <Typography
          variant="subtitle2"
          style={{
            fontSize: "var(--dt-header-font-size, 13px)",
            fontWeight: "var(--dt-header-font-weight, 600)",
            color: "var(--dt-header-color, inherit)",
          }}
        >
          {title}
        </Typography>

        {headerActions && (
          <Box display="flex" alignItems="center">
            {headerActions}
          </Box>
        )}
      </Box>
    );

    switch (displayHeader) {
      case "phone":
        return <Hidden smUp>{header}</Hidden>;
      case "mobile":
        return <Hidden lgUp>{header}</Hidden>;
      case "desktop":
        return <Hidden mdDown>{header}</Hidden>;
      case "all":
      default:
        return header;
    }
  };

  const getRowKey = (row, index) =>
    typeof rowKey === "function" ? rowKey(row, index) : (row[rowKey] ?? index);

  const renderCell = (column, row, index) => {
    const value = column.getValue
      ? column.getValue(row, index)
      : row[column.key];

    const content = column.render
      ? column.render(value, row, index)
      : (value ?? "—");

    const cell = (
      <TableCell
        align={column.align || "center"}
        style={{
          width: column.width,
          fontSize: "var(--dt-cell-font-size, 12px)",
          padding: "var(--dt-cell-padding, 0 10px)",
          textAlign: "var(--dt-cell-text-align, center)",
          ...column.cellStyle,
          verticalAlign: "middle",
          height: "100%",
        }}
      >
        {content}
      </TableCell>
    );

    if (column.desktopOnly) {
      return (
        <Hidden key={column.key} mdDown>
          {cell}
        </Hidden>
      );
    }

    if (column.mobileOnly) {
      return (
        <Hidden key={column.key} lgUp>
          {cell}
        </Hidden>
      );
    }

    return <React.Fragment key={column.key}>{cell}</React.Fragment>;
  };

  const renderHeaderCell = (column) => {
    const cell = (
      <TableCell
        align={column.align || "center"}
        style={{
          width: column.width,
          fontWeight: "var(--dt-head-cell-font-weight, 600)",
          fontSize: "var(--dt-head-cell-font-size, 12px)",
          textTransform: "var(--dt-head-cell-text-transform, uppercase)",
          color: "var(--dt-head-cell-color, inherit)",
          backgroundColor: "var(--dt-head-cell-bg, #eeeeee)",
          padding: "var(--dt-head-cell-padding, 0 7px 0 3px)",
          minHeight: "var(--dt-head-cell-min-height, 20px)",
          ...column.headerStyle,
        }}
      >
        {column.header}
      </TableCell>
    );

    if (column.desktopOnly) {
      return (
        <Hidden key={column.key} mdDown>
          {cell}
        </Hidden>
      );
    }

    if (column.mobileOnly) {
      return (
        <Hidden key={column.key} lgUp>
          {cell}
        </Hidden>
      );
    }

    return <React.Fragment key={column.key}>{cell}</React.Fragment>;
  };

  return (
    <Paper
      elevation={0}
      style={{
        width: "var(--dt-table-width, 98%)",
        margin: "var(--dt-table-margin, auto)",
        height: "fit-content",
        display: "var(--dt-wrapper-display, flex)",
        flexDirection: "column",
        border: "var(--dt-table-border, 1px solid #ddd)",
        overflow: "hidden",
        borderRadius: "var(--dt-table-border-radius, 0)",
      }}
    >
      {/* Header */}
      {(title || headerActions) && renderResponsiveHeader()}

      {/* Loading */}
      {loading ? (
        <Box
          flex={1}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress size={25} />
        </Box>
      ) : !data.length ? (
        /* No data */
        <Box
          flex={1}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Typography
            style={{
              fontSize: "var(--dt-row-font-size, 12px)",
              color: "#888",
            }}
          >
            {emptyMessage}
          </Typography>
        </Box>
      ) : (
        /* Table */
        <TableContainer
          style={{
            flex: 1,
            overflow: "auto",
          }}
        >
          <Table
            stickyHeader
            size="small"
            style={{
              width: "100%",
              tableLayout: "auto",
            }}
          >
            <TableHead>
              <TableRow
                style={{
                  height: "var(--dt-head-row-height, 23px)",
                  backgroundColor: "var(--dt-head-row-bg, transparent)",
                  minHeight: "var(--dt-head-row-min-height, 22px)",
                }}
              >
                {columns.map(renderHeaderCell)}

                {actions && (
                  <TableCell
                    align="center"
                    style={{
                      width: actions.width || 60,
                      fontWeight: "var(--dt-head-cell-font-weight, 600)",
                      fontSize: "var(--dt-head-cell-font-size, 12px)",
                      color: "var(--dt-head-cell-color, inherit)",
                      backgroundColor: "var(--dt-head-cell-bg, #eeeeee)",
                    }}
                  >
                    {actions.header || ""}
                  </TableCell>
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {data.map((row, index) => (
                <TableRow
                  key={getRowKey(row, index)}
                  hover
                  onClick={() => onRowClick && onRowClick(row, index)}
                  style={
                    equalRowHeight
                      ? {
                          // No height constraints — table auto-equalizes row heights
                          fontSize: "var(--dt-row-font-size, 12px)",
                          cursor: onRowClick ? "pointer" : "default",
                          transition: "var(--dt-row-transition, 0.15s)",
                        }
                      : {
                          height: "var(--dt-row-height, 40px)",
                          minHeight: "var(--dt-row-min-height, 20px)",
                          maxHeight: "var(--dt-row-max-height, 30px)",
                          fontSize: "var(--dt-row-font-size, 12px)",
                          cursor: onRowClick ? "pointer" : "default",
                          transition: "var(--dt-row-transition, 0.15s)",
                        }
                  }
                >
                  {columns.map((column) => renderCell(column, row, index))}

                  {actions && (
                    <TableCell
                      align="center"
                      style={{
                        padding: "var(--dt-cell-padding, 0 10px)",
                        fontSize: "var(--dt-cell-font-size, 12px)",
                        fontWeight: "var(--dt-cell-font-weight, 500)",
                      }}
                    >
                      {actions.render ? actions.render(row, index) : null}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default DynamicTable;
